/**
 * atomberg-coupons.js
 *
 * Wires up each `[data-atomberg-coupons]` instance:
 *
 *  1. Relocates `[data-atomberg-coupon-row]` siblings out of the scroller and
 *     into the sheet body's `[data-atomberg-coupons-sheet-list]` container.
 *  2. Manages the next-chevron visibility based on whether the scroller can
 *     scroll further (auto-hides when fully scrolled or content fits).
 *  3. Wires every `[data-atomberg-coupon-cta]` to open the parent's sheet
 *     (delegates to window.AtombergSheet).
 *  4. Wires every `[data-atomberg-coupon-copy]` to copy the code to clipboard
 *     and flash a toast.
 */
(function () {
  'use strict';

  /** @type {any} */
  var w = window;
  if (w._atombergCouponsInit) return;
  w._atombergCouponsInit = true;

  /**
   * Move all coupon-row `<li>` elements out of the scroller and into the
   * sheet body's list container. Coupon rows are static per-block config
   * (`show_in_sheet` toggle) — a block can be set to show in the card
   * ribbon but not the sheet, or vice versa — so the sheet list can end up
   * empty even though the scroller had cards. When that happens, hide the
   * "Atomberg Offer" group label too: it's rendered unconditionally by
   * Liquid (it can't know ahead of time whether any row will actually land
   * in the sheet), so left alone it shows a heading over nothing.
   * @param {HTMLElement} root
   */
  function relocateRows(root) {
    var scroller = root.querySelector('[data-atomberg-coupons-scroller]');
    var sheetList = root.querySelector('[data-atomberg-coupons-sheet-list]');
    if (!scroller || !sheetList) return;
    var list = sheetList;
    var rows = scroller.querySelectorAll('[data-atomberg-coupon-row]');
    rows.forEach(function (row) {
      list.appendChild(row);
    });

    var groupLabel = root.querySelector('[data-atomberg-coupons-sheet-group-label]');
    if (groupLabel instanceof HTMLElement) {
      groupLabel.hidden = list.querySelectorAll('[data-atomberg-coupon-row]').length === 0;
    }
  }

  /**
   * Sync prev/next arrow visibility based on current scroll position.
   * @param {HTMLElement} root
   */
  function syncNavVisibility(root) {
    var scroller = /** @type {HTMLElement | null} */ (
      root.querySelector('[data-atomberg-coupons-scroller]')
    );
    if (!scroller) return;
    var prev = root.querySelector('[data-atomberg-coupons-prev]');
    var next = root.querySelector('[data-atomberg-coupons-next]');
    var canScrollLeft = scroller.scrollLeft > 4;
    var canScrollRight = scroller.scrollLeft + scroller.clientWidth + 4 < scroller.scrollWidth;
    if (prev) {
      if (canScrollLeft) prev.removeAttribute('hidden');
      else prev.setAttribute('hidden', '');
    }
    if (next) {
      if (canScrollRight) next.removeAttribute('hidden');
      else next.setAttribute('hidden', '');
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function bindNav(root) {
    var scrollerEl = /** @type {HTMLElement | null} */ (
      root.querySelector('[data-atomberg-coupons-scroller]')
    );
    if (!scrollerEl) return;
    var scroller = scrollerEl;
    var prev = root.querySelector('[data-atomberg-coupons-prev]');
    var next = root.querySelector('[data-atomberg-coupons-next]');
    if (prev) {
      prev.addEventListener('click', function () {
        var amount = scroller.clientWidth * 0.8;
        scroller.scrollBy({ left: -amount, behavior: 'smooth' });
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        var amount = scroller.clientWidth * 0.8;
        scroller.scrollBy({ left: amount, behavior: 'smooth' });
      });
    }
    scroller.addEventListener('scroll', function () {
      syncNavVisibility(root);
    });
    window.addEventListener('resize', function () {
      syncNavVisibility(root);
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function bindCtas(root) {
    var sheetId = root.getAttribute('data-sheet-id');
    if (!sheetId) return;
    root.querySelectorAll('[data-atomberg-coupon-cta]').forEach(function (btn) {
      btn.setAttribute('data-atomberg-sheet-trigger', /** @type {string} */ (sheetId));
    });
  }

  /**
   * @param {string} text
   * @returns {Promise<boolean>}
   */
  function copyText(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(
        function () {
          return true;
        },
        function () {
          return fallbackCopy(text);
        }
      );
    }
    return Promise.resolve(fallbackCopy(text));
  }

  /**
   * @param {string} text
   * @returns {boolean}
   */
  function fallbackCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-1000px';
      document.body.appendChild(ta);
      ta.select();
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (_err) {
      return false;
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function bindCopyButtons(root) {
    var sheetId = root.getAttribute('data-sheet-id');
    var scope = /** @type {HTMLElement} */ ((sheetId && document.getElementById(sheetId)) || root);
    var toast = /** @type {HTMLElement | null} */ (
      scope.querySelector('[data-atomberg-coupons-toast]')
    );
    var msgEl = scope.querySelector('[data-atomberg-coupons-toast-message]');
    var dismissEl = scope.querySelector('[data-atomberg-coupons-toast-dismiss]');
    /** @type {number | null} */
    var toastTimer = null;
    /** @type {HTMLElement | null} */
    var lastCopiedBtn = null;

    function hideToast() {
      if (!toast) return;
      /** @type {HTMLElement} */ (toast).hidden = true;
      if (lastCopiedBtn) {
        lastCopiedBtn.removeAttribute('data-copied');
        lastCopiedBtn = null;
      }
      if (toastTimer !== null) {
        clearTimeout(toastTimer);
        toastTimer = null;
      }
    }

    if (dismissEl) {
      dismissEl.addEventListener('click', hideToast);
    }

    scope.addEventListener('click', function (e) {
      var target = e.target;
      if (!(target instanceof Element)) return;
      var btnEl = /** @type {HTMLElement | null} */ (target.closest('[data-atomberg-coupon-copy]'));
      if (!btnEl || !scope.contains(btnEl)) return;
      var btn = btnEl;
      var code = btn.getAttribute('data-atomberg-coupon-copy');
      if (!code) return;

      copyText(code).then(function (ok) {
        if (!ok) return;
        if (lastCopiedBtn && lastCopiedBtn !== btn) lastCopiedBtn.removeAttribute('data-copied');
        btn.setAttribute('data-copied', '');
        lastCopiedBtn = btn;
        if (!toast) return;
        var toastEl = /** @type {HTMLElement} */ (toast);
        if (msgEl) msgEl.textContent = 'Coupon code "' + code + '" copied to clipboard!';
        toastEl.hidden = false;
        if (toastTimer !== null) clearTimeout(toastTimer);
        toastTimer = window.setTimeout(hideToast, 3000);
      });
    });
  }

  /**
   * Move the sheet element to <body> so it isn't trapped inside the product
   * section's stacking context (which sits below the sticky header).
   * @param {HTMLElement} root
   */
  function teleportSheet(root) {
    var sheetId = root.getAttribute('data-sheet-id');
    if (!sheetId) return;
    var sheet = document.getElementById(sheetId);
    if (sheet && sheet.parentElement !== document.body) {
      document.body.appendChild(sheet);
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function init(root) {
    relocateRows(root);
    teleportSheet(root);
    bindCtas(root);
    bindNav(root);
    bindCopyButtons(root);
    syncNavVisibility(root);
  }

  function initAll() {
    document.querySelectorAll('[data-atomberg-coupons]').forEach(function (el) {
      var root = /** @type {HTMLElement} */ (el);
      /** @type {any} */
      var anyRoot = root;
      if (anyRoot._atombergCouponsBound) return;
      anyRoot._atombergCouponsBound = true;
      init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:load', initAll);
})();
